import { Component, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { AuthService } from '../../../core/auth/auth.service';
import { TenantBrandingService } from '../../../core/branding/tenant-branding.service';
import { I18nService } from '../../../core/i18n/i18n.service';
import { AppLanguage } from '../../../core/i18n/i18n.models';
import { TranslatePipe } from '../../../core/i18n/translate.pipe';

@Component({
  selector: 'app-register-page',
  standalone: true,
  imports: [ReactiveFormsModule, RouterLink, TranslatePipe],
  template: `
    <div class="register-page" [style.--tenant-primary]="tenantPrimaryColor" [style.--tenant-secondary]="tenantSecondaryColor" [style.--tenant-accent]="tenantAccentColor">
      <div class="register-page__card">
        <div class="register-page__header">
          <div>
            @if (brandingLogoUrl) {
              <img [src]="brandingLogoUrl" alt="Tenant logo" class="register-page__logo" />
            }
            <h1 class="register-page__title">{{ tenantDisplayName || ('auth.registerTitle' | t) }}</h1>
            <p class="register-page__subtitle">
              {{ 'auth.registerSubtitle' | t }} <strong>{{ tenantSlug }}</strong>
            </p>
          </div>
          <div class="register-page__lang">
            <label for="register-lang">{{ 'app.language' | t }}</label>
            <select id="register-lang" [value]="language" (change)="onLanguageChange($any($event.target).value)">
              @for (option of languageOptions; track option.code) {
                <option [value]="option.code">{{ option.label }}</option>
              }
            </select>
          </div>
        </div>

        <form [formGroup]="form" (ngSubmit)="onSubmit()" class="register-page__form">
          <div class="register-page__field">
            <label for="email">{{ 'auth.email' | t }}</label>
            <input
              id="email"
              type="email"
              formControlName="email"
              [placeholder]="'auth.enterEmail' | t"
            />
          </div>

          <div class="register-page__field">
            <label for="password">{{ 'auth.password' | t }}</label>
            <input
              id="password"
              type="password"
              formControlName="password"
              [placeholder]="'auth.enterPassword' | t"
            />
          </div>

          <div class="register-page__field">
            <label for="confirmPassword">{{ 'auth.confirmPassword' | t }}</label>
            <input
              id="confirmPassword"
              type="password"
              formControlName="confirmPassword"
              [placeholder]="'auth.confirmPassword' | t"
            />
          </div>

          @if (successText) {
            <div class="register-page__success">{{ successText | t }}</div>
          }

          @if (errorText) {
            <div class="register-page__error">{{ errorText | t }}</div>
          }

          <button type="submit" [disabled]="isSubmitting">
            {{ (isSubmitting ? 'auth.registering' : 'auth.register') | t }}
          </button>
        </form>

        <p class="register-page__footer">
          <a [routerLink]="['/', tenantSlug, 'login']">{{ 'auth.backToLogin' | t }}</a>
        </p>
      </div>
    </div>
  `,
  styles: [`
    .register-page {
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background:
        radial-gradient(circle at top left, color-mix(in srgb, var(--tenant-primary, #2563eb) 18%, transparent), transparent 34%),
        linear-gradient(160deg, color-mix(in srgb, var(--tenant-secondary, #0f172a) 7%, white), #f8fafc 55%);
      padding: 24px;
      box-sizing: border-box;
    }

    .register-page__card {
      width: 100%;
      max-width: 420px;
      background: #ffffff;
      border-radius: 16px;
      padding: 32px;
      box-sizing: border-box;
      box-shadow: 0 18px 40px color-mix(in srgb, var(--tenant-secondary, #0f172a) 16%, transparent);
    }

    .register-page__header {
      margin-bottom: 20px;
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 12px;
    }

    .register-page__logo {
      width: auto;
      max-width: 180px;
      max-height: 52px;
      display: block;
      margin-bottom: 10px;
      object-fit: contain;
    }

    .register-page__title {
      margin: 0 0 8px;
      font-size: 28px;
      color: var(--tenant-secondary, #111827);
    }

    .register-page__subtitle {
      margin: 0;
      color: #6b7280;
      font-size: 14px;
    }

    .register-page__lang {
      display: flex;
      flex-direction: column;
      gap: 6px;
      min-width: 90px;
    }

    .register-page__lang label {
      font-size: 12px;
      color: #374151;
      font-weight: 600;
    }

    .register-page__lang select {
      height: 34px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 8px;
      font-size: 13px;
      background: #fff;
    }

    .register-page__form {
      display: flex;
      flex-direction: column;
      gap: 14px;
    }

    .register-page__field {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .register-page__field label {
      font-size: 14px;
      font-weight: 500;
      color: #111827;
    }

    .register-page__field input {
      height: 42px;
      border: 1px solid #d1d5db;
      border-radius: 8px;
      padding: 0 12px;
      font-size: 14px;
      outline: none;
    }

    .register-page__field input:focus {
      border-color: var(--tenant-primary, #2563eb);
    }

    .register-page__error {
      color: #b91c1c;
      font-size: 14px;
    }

    .register-page__success {
      color: #166534;
      font-size: 14px;
    }

    .register-page__form button {
      height: 42px;
      border: none;
      border-radius: 8px;
      background: var(--tenant-primary, #2563eb);
      color: #ffffff;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
    }

    .register-page__form button:disabled {
      opacity: 0.7;
      cursor: default;
    }

    .register-page__footer {
      margin: 16px 0 0;
      font-size: 14px;
    }
  `],
})
export class RegisterPageComponent {
  private readonly authService = inject(AuthService);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly i18n = inject(I18nService);
  private readonly tenantBranding = inject(TenantBrandingService);

  protected readonly tenantSlug = this.route.snapshot.paramMap.get('companySlug')?.trim().toLowerCase() ?? '';
  protected isSubmitting = false;
  protected errorText: string | null = null;
  protected successText: string | null = null;
  protected readonly languageOptions = this.i18n.languages;
  protected tenantDisplayName = '';
  protected brandingLogoUrl: string | null = null;
  protected tenantPrimaryColor = '#2563eb';
  protected tenantSecondaryColor = '#0f172a';
  protected tenantAccentColor = '#f59e0b';

  protected get language(): AppLanguage {
    return this.i18n.language;
  }

  protected readonly form = this.formBuilder.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(8)]],
    confirmPassword: ['', [Validators.required]],
  });

  public constructor() {
    if (this.tenantSlug) {
      this.tenantBranding.getBranding(this.tenantSlug).subscribe((branding) => {
        const vars = this.tenantBranding.toCssVars(branding);
        this.tenantDisplayName = branding?.displayName ?? '';
        this.brandingLogoUrl = branding?.logoUrl ?? null;
        this.tenantPrimaryColor = vars['--tenant-primary'];
        this.tenantSecondaryColor = vars['--tenant-secondary'];
        this.tenantAccentColor = vars['--tenant-accent'];
      });
    }
  }

  protected onSubmit(): void {
    if (!this.tenantSlug) {
      this.errorText = 'auth.registrationUnavailable';
      return;
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const value = this.form.getRawValue();
    if (value.password !== value.confirmPassword) {
      this.errorText = 'auth.passwordMismatch';
      return;
    }

    this.errorText = null;
    this.successText = null;
    this.isSubmitting = true;

    this.authService
      .registerClient({
        companySlug: this.tenantSlug,
        email: value.email.trim(),
        password: value.password,
      })
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.successText = 'auth.registrationSuccess';
        },
        error: (error: unknown) => {
          this.errorText = this.resolveRegistrationErrorKey(error);
        },
      });
  }

  protected onLanguageChange(language: AppLanguage): void {
    this.i18n.setLanguage(language);
  }

  private resolveRegistrationErrorKey(error: unknown): string {
    if (!(error instanceof HttpErrorResponse)) {
      return 'auth.registrationFailed';
    }

    if (error.status === 409) {
      return 'auth.registrationEmailExists';
    }

    if (error.status === 403) {
      return 'auth.registrationEmailExists';
    }

    if (error.status === 400) {
      return 'auth.registrationUnavailable';
    }

    return 'auth.registrationFailed';
  }
}
